import * as React from 'react';
import { cn } from '../../lib/utils';

export type ChartConfig = Record<
  string,
  {
    label: string;
    color: string; // any valid CSS color
  }
>;

type ChartContextType = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextType | null>(null);

export function useChart() {
  const ctx = React.useContext(ChartContext);
  if (!ctx) {
    throw new Error('useChart must be used within a ChartContainer');
  }
  return ctx;
}

export function ChartContainer({ config, children, className }: { config: ChartConfig; children: React.ReactNode; className?: string }) {
  // Build CSS variables for colors
  const styleVars = React.useMemo(() => {
    const entries = Object.entries(config).map(([key, value]) => [`--chart-${key}`, value.color] as const);
    return Object.fromEntries(entries) as React.CSSProperties;
  }, [config]);

  return (
    <ChartContext.Provider value={{ config }}>
      <div className={cn('w-full', className)} style={styleVars}>
        {children}
      </div>
    </ChartContext.Provider>
  );
}

export function ChartLegend({ className }: { className?: string }) {
  const { config } = useChart();
  const items = Object.entries(config);
  if (!items.length) return null;
  return (
    <div className={cn('flex flex-wrap gap-3', className)}>
      {items.map(([key, value]) => (
        <div key={key} className='flex items-center gap-2'>
          <span className='inline-block h-3 w-3 rounded-sm' style={{ background: `var(--chart-${key})` }} />
          <span className='text-xs text-muted-foreground'>{value.label}</span>
        </div>
      ))}
    </div>
  );
}

export function ChartTooltipContent({ payload, label }: { payload?: Array<{ name: string; value: number; color?: string }>; label?: string | number }) {
  const { config } = useChart();
  if (!payload?.length) return null;
  return (
    <div className='rounded-md border bg-background p-2 shadow-sm'>
      {label !== undefined && <div className='mb-1 text-xs font-medium text-muted-foreground'>{label}</div>}
      <div className='space-y-1'>
        {payload.map((item) => {
          const key = item.name;
          const conf = config[key] ?? { label: key, color: item.color ?? '#999' };
          return (
            <div key={key} className='flex items-center justify-between gap-6'>
              <div className='flex items-center gap-2'>
                <span className='inline-block h-2.5 w-2.5 rounded-sm' style={{ background: `var(--chart-${key}, ${conf.color})` }} />
                <span className='text-xs text-muted-foreground'>{conf.label}</span>
              </div>
              <div className='text-xs font-medium tabular-nums'>{item.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
